import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    serviceNeeded: '',
    propertyType: '',
    preferredDate: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quotes`, formData);
      
      if (response.data.success) {
        toast.success("Quote Request Submitted!", {
          description: "We'll get back to you with a quote soon.",
        });

        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          serviceNeeded: '',
          propertyType: '',
          preferredDate: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error("Error", {
        description: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Info */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Request Your Free Quote
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Tell us what you need and we'll get back to you with a quote.
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-8">
              {[
                'Fast response within 24 hours',
                'No obligation quote',
                'Transparent pricing',
                'Customized cleaning plans'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Contact Image */}
            <div className="hidden lg:block rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/9462636/pexels-photo-9462636.jpeg"
                alt="Professional Cleaners"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="shadow-2xl border-0">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div>
                  <Label htmlFor="fullName" className="text-slate-900 font-medium mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="h-12 border-slate-300 focus:border-teal-600"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-slate-900 font-medium mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="h-12 border-slate-300 focus:border-teal-600"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-slate-900 font-medium mb-2 block">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(614) 555-0123"
                    className="h-12 border-slate-300 focus:border-teal-600"
                  />
                </div>

                {/* Service Needed */}
                <div>
                  <Label htmlFor="serviceNeeded" className="text-slate-900 font-medium mb-2 block">
                    Service Needed *
                  </Label>
                  <Select
                    required
                    value={formData.serviceNeeded}
                    onValueChange={(value) => handleSelectChange('serviceNeeded', value)}
                  >
                    <SelectTrigger className="h-12 border-slate-300">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential Cleaning</SelectItem>
                      <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                      <SelectItem value="deep">Deep Cleaning</SelectItem>
                      <SelectItem value="move">Move-In / Move-Out Cleaning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Type */}
                <div>
                  <Label htmlFor="propertyType" className="text-slate-900 font-medium mb-2 block">
                    Property Type *
                  </Label>
                  <Select
                    required
                    value={formData.propertyType}
                    onValueChange={(value) => handleSelectChange('propertyType', value)}
                  >
                    <SelectTrigger className="h-12 border-slate-300">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="commercial">Commercial Space</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred Date */}
                <div>
                  <Label htmlFor="preferredDate" className="text-slate-900 font-medium mb-2 block">
                    Preferred Service Date *
                  </Label>
                  <Input
                    id="preferredDate"
                    name="preferredDate"
                    type="date"
                    required
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    className="h-12 border-slate-300 focus:border-teal-600"
                  />
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-slate-900 font-medium mb-2 block">
                    Message / Details
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your cleaning needs..."
                    rows={4}
                    className="border-slate-300 focus:border-teal-600 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white text-lg font-semibold group"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      Request My Free Quote
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
